import { BoxV2 as Box, Flex, Tabs } from 'portal-commons';
import { FunctionComponent, useEffect, useMemo, useState } from 'react';

import { profileController, loginsManagerController, User } from '@/apis/core';
import {
  GermanyProfileBanner,
  GermanyTabAccount,
  GermanyTabEmailPreferences,
  GermanyTabUserManagement,
} from '../components';
import { CspProfile } from '@/types/user';
import { Loader } from '../../../../shared_elements';
import { useSelector } from 'react-redux';
import { RootReducer } from '@/store/types';
import { SelectItem } from '@/utils/types';

const ProfileManagementDE: FunctionComponent = () => {
  const countries = useSelector(
    (state: RootReducer) => state.CSPReducer.countries
  );

  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('account');
  const [profile, setProfile] = useState<CspProfile | undefined>();
  const [countryName, setCountryName] = useState<string>('');
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<string[]>([]);

  const tabs = useMemo(
    () => [
      { id: 'account', label: 'Account' },
      { id: 'emails', label: 'Email Preferences' },
      { id: 'users', label: 'User Management' },
    ],
    []
  );

  const findCountryName = (countryOptions: SelectItem[], country: string) => {
    return (
      countryOptions.find((item) => item.value === country)?.label || country
    );
  };

  const fetchInitialData = async () => {
    const [profileResult, usersResult, rolesResult] = await Promise.allSettled([
      profileController.getProfile(),
      loginsManagerController.getUsers(),
      loginsManagerController.getRoles(),
    ]);

    if (profileResult.status === 'fulfilled') {
      setProfile(profileResult.value);
    }
    if (usersResult.status === 'fulfilled') {
      setUsers(usersResult.value);
    }
    if (rolesResult.status === 'fulfilled' && rolesResult.value) {
      setRoles(rolesResult.value);
    }

    setLoading(false);
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (countries.length && profile) {
      setCountryName(findCountryName(countries, profile.country));
    }
  }, [countries, profile]);

  return loading || !profile ? (
    <Loader />
  ) : (
    <Flex
      sx={{ flexDirection: 'column', rowGap: 40, p: '10px 66px 55px 71px' }}
      data-testid="profileManagementContainer"
    >
      <GermanyProfileBanner
        companyName={profile?.companyName}
        displayName={profile?.displayName}
      />

      <Tabs
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={tabs}
        portal="csp"
        data-testid="profileTabs"
      />

      <Box>
        {activeTab === 'account' && (
          <GermanyTabAccount
            profile={profile}
            countryName={countryName}
            currentPortalUsers={users?.length}
          />
        )}
        {activeTab === 'emails' && (
          <GermanyTabEmailPreferences profile={profile} onUpdate={setProfile} />
        )}
        {activeTab === 'users' && (
          <GermanyTabUserManagement
            profile={profile}
            users={users}
            onUsersUpdate={setUsers}
            roles={roles}
          />
        )}
      </Box>
    </Flex>
  );
};

export default ProfileManagementDE;
